import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  IconButton,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Close,
  ImportExport,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import "./AssetsManagerNew.css";

/* ---------- EMPTY FORM ---------- */
const emptyForm = {
  location: "",
  assetsType: "",
  serialNo: "",
  vendor: "",
  description: "",
  quantity: "",
  validityFrom: "",
  validityTo: "",
  status: "",
  remarks: "",
  updatedBy: "",
  updatedDate: "",
};

/* ---------- SAMPLE DATA ---------- */
const initialData = [
  {
    location: "Hyderabad",
    assetsType: "Hardware",
    serialNo: "HYD-HW-001",
    vendor: "Dell",
    description: "Laptop - Latitude 5420",
    quantity: "25",
    validityFrom: "2024-04-01",
    validityTo: "2027-03-31",
    status: "Available",
    remarks: "Assigned to IT team",
    updatedBy: "Admin",
    updatedDate: "2025-01-10",
  },
  {
    location: "Pune",
    assetsType: "Software",
    serialNo: "PUN-SW-014",
    vendor: "Microsoft",
    description: "Office 365 License",
    quantity: "120",
    validityFrom: "2025-01-01",
    validityTo: "2026-12-31",
    status: "Available",
    remarks: "Annual Subscription",
    updatedBy: "HR Admin",
    updatedDate: "2025-01-05",
  },
];

const AssetsManagerNew = () => {
  const [data, setData] = useState(initialData);
  const [form, setForm] = useState(emptyForm);
  const [mode, setMode] = useState("add");
  const [open, setOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const fileInputRef = useRef(null);

  const openModal = (type, row = null, index = null) => {
    setMode(type);
    setForm(row || emptyForm);
    setEditIndex(index);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
    setForm(emptyForm);
    setEditIndex(null);
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = () => {
    if (mode === "add") {
      setData([...data, form]);
    } else {
      const updated = [...data];
      updated[editIndex] = form;
      setData(updated);
    }
    closeModal();
  };

  const handleDelete = (index) => {
    if (window.confirm("Delete this asset?")) {
      setData(data.filter((_, i) => i !== index));
    }
  };

  const handleExport = () => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Assets");
    const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buffer]), "assets.xlsx");
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const wb = XLSX.read(evt.target.result, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      setData(XLSX.utils.sheet_to_json(sheet));
    };
    reader.readAsArrayBuffer(file);
  };

  return (
    <div className="amx-wrapper">
      <input
        type="file"
        hidden
        ref={fileInputRef}
        onChange={handleImport}
      />

      {/* HEADER */}
      <div className="amx-header">
        <h2>Assets Management</h2>
        <div className="amx-actions">
          <Button onClick={() => fileInputRef.current.click()} startIcon={<ImportExport />}>
            Import
          </Button>
          <Button onClick={handleExport} startIcon={<ImportExport />}>
            Export
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => openModal("add")}>
            Add Asset
          </Button>
        </div>
      </div>

      {/* TABLE */}
      <TableContainer component={Paper} className="amx-table-box">
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell><Checkbox /></TableCell>
              {Object.keys(emptyForm).map((key) => (
                <TableCell key={key}>{key}</TableCell>
              ))}
              <TableCell align="center">Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((row, index) => (
              <TableRow key={index}>
                <TableCell><Checkbox /></TableCell>
                {Object.keys(emptyForm).map((key) => (
                  <TableCell key={key}>{row[key]}</TableCell>
                ))}
                <TableCell align="center">
                  <IconButton onClick={() => openModal("view", row)}>
                    <Visibility />
                  </IconButton>
                  <IconButton onClick={() => openModal("edit", row, index)} color="warning">
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(index)} color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* MODAL */}
      <Modal open={open} onClose={closeModal}>
        <Box className="amx-modal">
          <div className="amx-modal-header">
            <h3>{mode.toUpperCase()} ASSET</h3>
            <IconButton onClick={closeModal}><Close /></IconButton>
          </div>

          <div className="amx-modal-grid">
            {Object.keys(emptyForm).map((key) => (
              <TextField
                key={key}
                name={key}
                label={key}
                value={form[key]}
                onChange={handleChange}
                disabled={mode === "view"}
                size="small"
              />
            ))}
          </div>

          {mode !== "view" && (
            <div className="amx-modal-footer">
              <Button variant="contained" onClick={handleSave}>
                Save
              </Button>
            </div>
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default AssetsManagerNew;
